import type { Nullable, TimelineSection } from "@msuite/utils/types";
import { useQueryState } from "nuqs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BlankSlate } from "../blank-slate";
import { Virtuoso, type VirtuosoHandles } from "../libs/virtuoso";
import { LoadingScreen } from "../loading-screen";
import { TimelineContext } from "./context";
import { TimelineDateRow } from "./timeline-date-row";
import { TimelineLine } from "./timeline-line";
import { TimelineRow } from "./timeline-row";
import { TimelineSearch } from "./timeline-search";
import { useVirtualizeTimeline } from "./use-virtualize-timeline";

/** Props Interface */
interface TimelineProps<T extends { id: string }, U> {
	timeline: Nullable<Array<TimelineSection<T, U>>>;
	renderItem: (type: U, item: T) => React.ReactNode;
	renderItemMarker?: (type: U, item: T) => React.ReactNode;
	isLoading: boolean;
	refetch: () => Promise<void>;
	showEmptySections?: boolean;
	controls?: React.ReactNode;
	virtuoso?: VirtuosoHandles;
	onEndReached?: () => Promise<void> | void;
}

export function Timeline<T extends { id: string }, U>({
	timeline = [],
	renderItem,
	renderItemMarker,
	isLoading,
	refetch,
	controls,
	showEmptySections = false,
	virtuoso,
	onEndReached,
}: TimelineProps<T, U>) {
	/** State */
	const [isScrolling, setIsScrolling] = useState(false);
	const [searchValue] = useQueryState("searchValue", {
		defaultValue: "",
	});

	/** Memoized */
	const { virtualized } = useVirtualizeTimeline({ timeline, searchValue, showEmptySections });

	useEffect(() => {
		for (let index = 0; index < virtualized.length; index++) {
			const item = virtualized[index];
			if (!item) continue;
			if (item.type !== "date") continue;
			virtuoso?.indexMap?.current?.set(`date-${item.date}`, index);
		}
	}, [virtualized]);

	const components = useMemo(
		() => ({
			Footer,
		}),
		[],
	);

	const itemContent = useCallback(
		(
			index: number,
			currentItem: {
				type: "date" | "item" | "blank";
				date?: string;
				item?: unknown;
			},
		) => {
			const isLast = index === virtualized.length - 1;

			if (currentItem.type === "date") {
				return (
					<TimelineDateRow
						date={currentItem.date ?? ""}
						withoutLine={index === virtualized.length - 1}
					/>
				);
			}

			/** Computed */
			if (currentItem.type === "blank") {
				return (
					<TimelineRow>
						<TimelineLine withoutLine={index === virtualized.length - 1} />
						<div className="pb-4">
							<BlankSlate />
						</div>
					</TimelineRow>
				);
			}

			if (!currentItem.item) return null;

			return (
				<TimelineItem
					item={currentItem.item as { type: U; item: T }}
					isLast={isLast}
					renderItem={renderItem}
					renderItemMarker={renderItemMarker}
				/>
			);
		},
		[virtualized.length, renderItem, renderItemMarker],
	);

	/** Render */
	return (
		<TimelineContext.Provider value={{ refetch }}>
			<div className="gap-0 pl-2 pt-1 h-full v-stack">
				<div className="top-0 z-20 sticky justify-between items-center col-span-2 pb-4 h-stack">
					<TimelineSearch />
					{controls && controls}
				</div>
				<React.Fragment>
					{isLoading && <LoadingScreen />}
					{virtualized.length === 0 ? (
						<div className="bottom-space flex-1 pt-2 h-full">
							<BlankSlate />
						</div>
					) : (
						<Virtuoso
							ref={virtuoso?.ref}
							className="scrollbar-hidden gap-0 overflow-y-auto v-stack"
							data={virtualized}
							totalCount={virtualized.length}
							context={{ isScrolling }}
							isScrolling={setIsScrolling}
							itemContent={itemContent}
							components={components}
							endReached={onEndReached}
						/>
					)}
				</React.Fragment>
			</div>
		</TimelineContext.Provider>
	);
}

const TimelineItem = React.memo(function TimelineItem({
	item,
	isLast,
	renderItem,
	renderItemMarker,
}: {
	item: { type: any; item: any };
	isLast?: boolean;
	renderItem: (type: any, item: any) => React.ReactNode;
	renderItemMarker?: (type: any, item: any) => React.ReactNode;
}) {
	const { type, item: data } = item;

	// The renderer function is now stable within this component's render scope
	const renderer = renderItemMarker ? () => renderItemMarker(type, data) : undefined;

	return (
		<TimelineRow>
			<TimelineLine
				renderItemMarker={renderer}
				withoutLine={isLast}
			/>
			<div className="pb-4">{renderItem(type, data)}</div>
		</TimelineRow>
	);
});

function Footer() {
	return <div className="bottom-space" />;
}
